import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  Typography,
  Box,
  Alert,
  Avatar,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import type { IMaintenanceScheduleDialogProps } from "../../types/equipment";
import { tokens } from "../../theme";

const MaintenanceScheduleDialog: React.FC<IMaintenanceScheduleDialogProps> = ({
  open,
  onClose,
  schedule,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  if (!schedule) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, scale: 0.9 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 },
        transition: { duration: 0.3 },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Maintenance Schedule Details</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {schedule.priority?.toLowerCase() === "high" && (
          <Alert
            severity="warning"
            icon={<WarningIcon />}
            sx={{ mb: 3, bgcolor: "#FFF3CD", color: "#856404" }}
          >
            High Maintenance Priority
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="h5" sx={{ color: colors.primary[300], mb: 1 }}>
              Task
            </Typography>
            <Box>
              <Typography variant="body1" fontWeight="bold">
                {schedule.title}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Typography variant="h6" sx={{ color: colors.primary[300], mb: 1 }}>
              Start Date
            </Typography>
            <Typography sx={{ fontWeight: "bold" }}>
              {new Date(schedule.startDate).toLocaleString()}
            </Typography>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Typography variant="h6" sx={{ color: colors.primary[300], mb: 1 }}>
              End Date
            </Typography>
            <Typography sx={{ fontWeight: "bold" }}>
              {new Date(schedule.endDate).toLocaleString()}
            </Typography>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Typography variant="h6" sx={{ color: colors.primary[300], mb: 1 }}>
              Assigned To
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar>
                <PersonIcon />
              </Avatar>
              <Typography fontWeight="bold">{schedule.assignedTo}</Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Typography variant="h6" sx={{ color: colors.primary[300], mb: 1 }}>
              Expected Downtime
            </Typography>
            <Typography sx={{ fontWeight: "bold" }}>
              {schedule.expectedDowntime || "Not specified"}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" sx={{ color: colors.primary[300], mb: 1 }}>
              Maintenance Description
            </Typography>
            <Box
              sx={{
                backgroundColor: colors.primary[800],
                p: 2,
                borderRadius: 2,
              }}
            >
              <Typography sx={{ fontWeight: "bold" }}>
                {schedule.details}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          sx={{
            backgroundColor: colors.primary[800],
            borderRadius: 2,
            color: colors.primary[100],
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaintenanceScheduleDialog;
